import React, { useState } from "react";
import BackButton from "../shared/backButton";
import appConstants from "../shared/config/constants.json";

import "./LegalPage.css";

const PrivacyPolicy = () => {
  const [expandedSections, setExpandedSections] = useState({});
  const [showPrintButton, setShowPrintButton] = useState(true);
  const { MAYAMAYA_PROD_WHITELABEL_SUPPORT_URL } = appConstants;

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleNavClick = (e, sectionId) => {
    e.preventDefault();

    // open the section
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: true,
    }));

    // scroll to the section
    const el = document.getElementById(sectionId);
    if (el) {
      const yOffset = -80; // adjust offset here
      const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({
        top: y,
        behavior: "smooth",
      });
    }
  };

  const handleBack = () => {
    window.history.back();
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = "/Privacy-Policy.pdf"; // make sure this file is in your public folder
    link.download = "Privacy-Policy.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const sections = [
    { id: "info-collect", title: "Information We Collect" },
    { id: "how-use", title: "How We Use Your Information" },
    { id: "sharing", title: "How We Share Your Information" },
    { id: "technology", title: "Use of Technology for Tracking" },
    { id: "analytics", title: "Data Usage and Analytics" },
    { id: "third-party", title: "Third-Party Websites and Links" },
    { id: "access-rights", title: "Access, Control, and Your Rights" },
    { id: "security", title: "Data Security" },
    { id: "children", title: "Children's Privacy" },
    { id: "retention", title: "Data Retention" },
    { id: "contact", title: "Contact Information" },
  ];

  return (
    <div className="legal-wrapper">
      <div className="legal-main-content">
        <BackButton />
        {/* Header Section */}
        <div className="legal-hero-section">
          <div className="legal-hero-inner">
            <h1 className="legal-hero-title">Privacy Policy</h1>
            <p className="legal-hero-updated">Last Updated: April 1, 2026</p>
            {/* <button className="legal-print-button" onClick={handleDownload}>
              Download as PDF
            </button> */}
          </div>
        </div>

        {/* Acceptance Notice */}
        <div className="legal-container">
          <div className="legal-acceptance-notice">
            <h2 className="legal-acceptance-title">Your Privacy Matters</h2>
            <p className="legal-acceptance-text">
              Catenate ("we," "us," and "our") respects the privacy of its users
              ("you") and has developed this Privacy Policy to demonstrate our
              commitment to protecting your privacy. By using our website
              www.mayamaya.ai or the MayaMaya application, you are accepting the
              practices described in this Privacy Policy. Please read this
              carefully before using our Service.
            </p>
          </div>

          {/* Table of Contents */}
          <div className="legal-toc-section">
            <h2 className="legal-toc-title">Quick Navigation</h2>
            <div className="legal-toc-grid">
              {sections.map((section, index) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="legal-toc-link"
                  onClick={(e) => handleNavClick(e, section.id)}
                >
                  {index + 1}. {section.title}
                </a>
              ))}
            </div>
          </div>

          {/* Main Content Sections */}
          <section id="info-collect" className="legal-section">
            <button
              className="legal-section-toggle"
              onClick={() => toggleSection("info-collect")}
            >
              <span className="legal-toggle-icon">
                {expandedSections["info-collect"] ? "−" : "+"}
              </span>
              <h2 className="legal-section-title">Information We Collect</h2>
            </button>
            {expandedSections["info-collect"] && (
              <div className="legal-section-content">
                <div className="legal-subsection">
                  <h3>Information You Provide</h3>
                  <p>
                    In order to register as a user with us, you will be asked to
                    sign in either using your Google or Microsoft login or
                    create an account by using your email. If you choose to use
                    the Google or Microsoft login option, you authorize us to
                    access certain Google or Microsoft account information, such
                    as your public profile, email address, interests, likes,
                    gender, birthday, education history, relationship interests,
                    current city, photos and personal description.
                  </p>
                  <p>
                    We may collect and store any personal information you
                    provide while using our Service. This may include
                    identifying information, such as your name, address, email
                    address and telephone number, and financial information if
                    you transact business with us. You may also provide us
                    photos, a personal description and information about your
                    gender and personality type.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>Information Collected Automatically</h3>
                  <p>
                    We automatically collect information from your browser or
                    device when you visit our Service. This information could
                    include your IP address, device ID and type, your browser
                    type and language, the operating system used by your device,
                    access times, your mobile device's geographic location while
                    our application is actively running, and the referring
                    website address.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>Cookies and Use of Cookie Data</h3>
                  <p>
                    When you visit our Service, we may assign your device one or
                    more cookies to facilitate access to our Service and to
                    personalize your experience. Through the use of a cookie, we
                    also may automatically collect information about your
                    activity on our Service, such as the pages you visit, the
                    time and date of your visits and the links you click. If we
                    advertise, we (or third parties) may use certain data
                    collected on our Service to show you our advertisements on
                    other sites or applications.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>Pixel Tags</h3>
                  <p>
                    We embed pixel tags (also called web beacons or clear GIFs)
                    on web pages, ads, and emails. These tiny, invisible
                    graphics are used to access cookies and track user
                    activities (such as how many times a page is viewed). We use
                    pixel tags to measure the popularity of our features and
                    services. Ad companies also use pixel tags to measure the
                    number of ads displayed and their performance.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>Mobile Device IDs</h3>
                  <p>
                    If you're using our app, we use mobile device IDs (the
                    unique identifier assigned to a device by the manufacturer),
                    or Advertising IDs (for iOS 6 and later), instead of
                    cookies, to recognize you. We do this to store your
                    preferences and track your use of our app. Unlike cookies,
                    device IDs cannot be deleted, but Advertising IDs can be
                    reset in "Settings" on your iPhone. Ad companies also use
                    device IDs or Advertising IDs to track your use of the app,
                    track the number of ads displayed, measure ad performance
                    and display ads that are more relevant to you.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>
                    Information Collected by Third-Parties for Advertising
                  </h3>
                  <p>
                    We may allow service providers, advertising companies and ad
                    networks, and other third parties to display advertisements
                    on our Service. These companies may use tracking
                    technologies, such as cookies or web beacons, to collect
                    information about users who view or interact with their
                    advertisements. We do not provide any non-anonymized
                    personal information to third parties. Some of these
                    third-party advertising companies may be advertising
                    networks that are members of the Network Advertising
                    Initiative, which offers a single location to opt out of ad
                    targeting from member companies at
                    www.networkadvertising.org. Opting out will not decrease the
                    number of advertisements you see.
                  </p>
                </div>
              </div>
            )}
          </section>

          <section id="how-use" className="legal-section">
            <button
              className="legal-section-toggle"
              onClick={() => toggleSection("how-use")}
            >
              <span className="legal-toggle-icon">
                {expandedSections["how-use"] ? "−" : "+"}
              </span>
              <h2 className="legal-section-title">
                How We Use Your Information
              </h2>
            </button>
            {expandedSections["how-use"] && (
              <div className="legal-section-content">
                <p>
                  In general, we may use information that we collect about you
                  to:
                </p>
                <ul className="legal-content-list">
                  <li>
                    Deliver and improve our products and services, and manage
                    our business
                  </li>
                  <li>
                    Manage your account and provide you with customer support
                  </li>
                  <li>
                    Perform research and analysis about your use of, or interest
                    in, our or others' products, services, or content
                  </li>
                  <li>
                    Communicate with you by email, postal mail, telephone and/or
                    mobile devices about products or services that may be of
                    interest to you either from us or other third parties
                  </li>
                  <li>
                    Develop, display, and track content and advertising tailored
                    to your interests on our Service and other sites, including
                    providing advertisements to you when you visit other sites
                  </li>
                  <li>Website or mobile application analytics</li>
                  <li>
                    Verify your eligibility and deliver prizes in connection
                    with contests and sweepstakes
                  </li>
                  <li>Enforce or exercise any rights in our Terms of Use</li>
                  <li>
                    Perform functions or services as otherwise described to you
                    at the time of collection
                  </li>
                </ul>
                <p>
                  In all circumstances, we may perform these functions directly
                  or use a third party vendor to perform these functions on our
                  behalf who will be obligated to use your personal information
                  only to perform services for us. Also, if you access our
                  Service from a third party social platform, such as Facebook,
                  Snapchat, Instagram or Whatsapp, we may share non-personal
                  information with that platform to the extent permitted by your
                  agreement with it and its privacy settings.
                </p>
              </div>
            )}
          </section>

          <section id="sharing" className="legal-section">
            <button
              className="legal-section-toggle"
              onClick={() => toggleSection("sharing")}
            >
              <span className="legal-toggle-icon">
                {expandedSections["sharing"] ? "−" : "+"}
              </span>
              <h2 className="legal-section-title">
                How We Share Your Information
              </h2>
            </button>
            {expandedSections["sharing"] && (
              <div className="legal-section-content">
                <div className="legal-subsection">
                  <h3>Information Shared with Other Users</h3>
                  <p>
                    When you register as a user of MayaMaya, your profile will
                    be viewable by other users of the Service. Other users may
                    be able to view information you have provided to us directly
                    or through our other future products at your discretion,
                    such as your photos, any additional photos you upload, your
                    first name, gender, your personal description and
                    personality type.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>Personal Information Sharing</h3>
                  <p>
                    We may share personal information (name, email, phone and IP
                    address) with:
                  </p>
                  <ul className="legal-content-list">
                    <li>
                      Other trusted advertisers, third party marketers and our
                      affiliated entities for their online and/or offline
                      marketing programs. As a result, you may receive
                      information on additional products, services and offers
                      that may be of interest to you via email
                    </li>
                    <li>
                      Service providers who assist us with fulfilling orders,
                      providing customer service and marketing assistance,
                      performing business and sales analysis, ad tracking and
                      analytics, member screenings, supporting our Service
                      functionality, and supporting contests, sweepstakes,
                      surveys and other features. These service providers may
                      have access to personal information needed to perform
                      their functions but are not permitted to share or use such
                      information for any other purposes
                    </li>
                  </ul>
                </div>

                <div className="legal-subsection">
                  <h3>Other Situations</h3>
                  <p>
                    We may disclose your information, including personal
                    information:
                  </p>
                  <ul className="legal-content-list">
                    <li>
                      In response to a subpoena or similar investigative demand,
                      a court order, or a request for cooperation from a law
                      enforcement or other government agency; to establish or
                      exercise our legal rights; to defend against legal claims;
                      or as otherwise required by law. In such cases, we may
                      raise or waive any legal objection or right available to
                      us
                    </li>
                    <li>
                      When we believe disclosure is appropriate in connection
                      with efforts to investigate, prevent, or take other action
                      regarding illegal activity, suspected fraud or other
                      wrongdoing; to protect and defend the rights, property or
                      safety of our company, our users, our employees, or
                      others; to comply with applicable law or cooperate with
                      law enforcement; or to enforce our Terms of Use or other
                      agreements or policies
                    </li>
                    <li>
                      In connection with a substantial corporate transaction,
                      such as the sale of our business, a divestiture, merger,
                      consolidation, or asset sale, or in the unlikely event of
                      bankruptcy
                    </li>
                  </ul>
                </div>

                <div className="legal-subsection">
                  <h3>Aggregated and Non-Personal Information</h3>
                  <p>
                    We may use and share non-personal information we collect
                    under any of the above circumstances. We may also share it
                    with other Online Commerce Group companies and third parties
                    to develop and deliver targeted advertising on our Service
                    and on websites or applications of third parties, and to
                    analyze and report on advertising you see. We may combine
                    non-personal information we collect with additional
                    non-personal information collected from other sources. We
                    also may share aggregated, non-personal information, or
                    personal information in hashed, non-human readable form,
                    with third parties, including advisors, advertisers and
                    investors, for the purpose of conducting general business
                    analysis or other business purposes.
                  </p>
                  <p>
                    To opt-out of cookies that may be set by third party data or
                    advertising partners, please go to
                    http://www.aboutads.info/choices/.
                  </p>
                </div>
              </div>
            )}
          </section>

          <section id="technology" className="legal-section">
            <button
              className="legal-section-toggle"
              onClick={() => toggleSection("technology")}
            >
              <span className="legal-toggle-icon">
                {expandedSections["technology"] ? "−" : "+"}
              </span>
              <h2 className="legal-section-title">
                Use of Technology for Tracking
              </h2>
            </button>
            {expandedSections["technology"] && (
              <div className="legal-section-content">
                <p>
                  We use various technologies to collect information from your
                  device and about your activities on our Service to enhance
                  your experience and understand usage patterns.
                </p>
              </div>
            )}
          </section>

          <section id="analytics" className="legal-section">
            <button
              className="legal-section-toggle"
              onClick={() => toggleSection("analytics")}
            >
              <span className="legal-toggle-icon">
                {expandedSections["analytics"] ? "−" : "+"}
              </span>
              <h2 className="legal-section-title">Data Usage and Analytics</h2>
            </button>
            {expandedSections["analytics"] && (
              <div className="legal-section-content">
                <p>
                  We use analytics tools and third-party analytics services to
                  measure and analyze user behavior and improve our Service
                  performance.
                </p>
              </div>
            )}
          </section>

          <section id="third-party" className="legal-section">
            <button
              className="legal-section-toggle"
              onClick={() => toggleSection("third-party")}
            >
              <span className="legal-toggle-icon">
                {expandedSections["third-party"] ? "−" : "+"}
              </span>
              <h2 className="legal-section-title">
                Third-Party Websites and Links
              </h2>
            </button>
            {expandedSections["third-party"] && (
              <div className="legal-section-content">
                <p>
                  There are a number of places on our Service where you may
                  click on a link to access other websites that do not operate
                  under this Privacy Policy. For example, if you click on an
                  advertisement on our Service, you may be taken to a website
                  that we do not control. These third-party websites may
                  independently solicit and collect information, including
                  personal information, from you and, in some instances, provide
                  us with information about your activities on those websites.
                  We recommend that you consult the privacy statements of all
                  third-party websites you visit by clicking on the "privacy"
                  link typically located at the bottom of the webpage you are
                  visiting.
                </p>
              </div>
            )}
          </section>

          <section id="access-rights" className="legal-section">
            <button
              className="legal-section-toggle"
              onClick={() => toggleSection("access-rights")}
            >
              <span className="legal-toggle-icon">
                {expandedSections["access-rights"] ? "−" : "+"}
              </span>
              <h2 className="legal-section-title">
                Access, Control, and Your Rights
              </h2>
            </button>
            {expandedSections["access-rights"] && (
              <div className="legal-section-content">
                <div className="legal-subsection">
                  <h3>How You Can Access Your Information</h3>
                  <p>
                    If you have a MayaMaya account, you have the ability to
                    review and update your personal information within the
                    Service by opening your account and going to settings. You
                    also may close your account at any time by visiting the
                    "Settings" page for your account. If you close your account,
                    we will retain certain information associated with your
                    account for analytical purposes and recordkeeping integrity,
                    as well as to prevent fraud, enforce our Terms of Use, take
                    actions we deem necessary to protect the integrity of our
                    Service or our users, or take other actions otherwise
                    permitted by law.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>Your Choices About Collection and Use</h3>
                  <ul className="legal-content-list">
                    <li>
                      You can choose not to provide us with certain information,
                      but that may result in you being unable to use certain
                      features of our Service because such information may be
                      required in order for you to register as user; purchase
                      products or services; ask a question; or initiate other
                      transactions
                    </li>
                    <li>
                      Our Service may also deliver notifications to your phone
                      or mobile device. You can disable these notifications by
                      going into "App Settings" on the app or by changing the
                      settings on your mobile device
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </section>

          <section id="security" className="legal-section">
            <button
              className="legal-section-toggle"
              onClick={() => toggleSection("security")}
            >
              <span className="legal-toggle-icon">
                {expandedSections["security"] ? "−" : "+"}
              </span>
              <h2 className="legal-section-title">Data Security</h2>
            </button>
            {expandedSections["security"] && (
              <div className="legal-section-content">
                <div className="legal-subsection">
                  <h3>How We Protect Your Personal Information</h3>
                  <p>
                    We take security measures to help safeguard your personal
                    information from unauthorized access and disclosure.
                    However, no system can be completely secure. Therefore,
                    although we take steps to secure your information, we do not
                    promise, and you should not expect, that your personal
                    information, chats, or other communications will always
                    remain secure. Users should also take care with how they
                    handle and disclose their personal information and should
                    avoid sending personal information through insecure email.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>Information You Provide Publicly</h3>
                  <p>
                    We provide areas on our Service where you can post
                    information about yourself and others and communicate with
                    others. Such postings are governed by our Terms of Use.
                    Also, whenever you voluntarily disclose personal information
                    on publicly-viewable pages, that information will be
                    publicly available and can be collected and used by others.
                    For example, if you post your email address, you may receive
                    unsolicited messages. We cannot control who reads your
                    posting or what other users may do with the information you
                    voluntarily post, so we encourage you to exercise discretion
                    and caution with respect to your personal information.
                  </p>
                </div>
              </div>
            )}
          </section>

          <section id="children" className="legal-section">
            <button
              className="legal-section-toggle"
              onClick={() => toggleSection("children")}
            >
              <span className="legal-toggle-icon">
                {expandedSections["children"] ? "−" : "+"}
              </span>
              <h2 className="legal-section-title">Children's Privacy</h2>
            </button>
            {expandedSections["children"] && (
              <div className="legal-section-content">
                <p>
                  Although our Service is a general audience Service, we
                  restrict the use of our service to individuals age 7 and
                  above. We do not knowingly collect, maintain, or use personal
                  information from children under the age of 7.
                </p>
              </div>
            )}
          </section>

          <section id="retention" className="legal-section">
            <button
              className="legal-section-toggle"
              onClick={() => toggleSection("retention")}
            >
              <span className="legal-toggle-icon">
                {expandedSections["retention"] ? "−" : "+"}
              </span>
              <h2 className="legal-section-title">Data Retention</h2>
            </button>
            {expandedSections["retention"] && (
              <div className="legal-section-content">
                <p>
                  We retain your personal information for as long as necessary
                  to provide our Service, comply with legal obligations, and
                  pursue our legitimate business interests. The retention period
                  may vary depending on the context of the processing and the
                  purposes for which we collected the information.
                </p>
              </div>
            )}
          </section>

          <section id="contact" className="legal-section">
            <button
              className="legal-section-toggle"
              onClick={() => toggleSection("contact")}
            >
              <span className="legal-toggle-icon">
                {expandedSections["contact"] ? "−" : "+"}
              </span>
              <h2 className="legal-section-title">Contact Information</h2>
            </button>
            {expandedSections["contact"] && (
              <div className="legal-section-content">
                <p>
                  If you have any questions about this Privacy Policy, please
                  contact{" "}
                  <a href={MAYAMAYA_PROD_WHITELABEL_SUPPORT_URL}>
                    <strong>MayaMaya Support</strong>
                  </a>
                  .
                </p>
              </div>
            )}
          </section>

          {/* Changes Notice */}
          <div className="legal-info-box">
            <h3>Changes to This Privacy Policy</h3>
            <p>
              We will occasionally update this Privacy Policy. When we post
              changes to this Privacy Policy, we will revise the "Last Updated"
              date at the top of this Privacy Policy. We recommend that you
              check our Service from time to time to inform yourself of any
              changes in this Privacy Policy or any of our other policies.
            </p>
          </div>

          {/* No Rights Section */}
          <div className="legal-info-box">
            <h3>No Rights of Third Parties</h3>
            <p>
              This Privacy Policy does not create rights enforceable by third
              parties or require disclosure of any personal information relating
              to users of the website.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
